import { FC, useEffect, useRef, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, RefreshCw, X, Settings, Sparkles, Activity, Loader2, Wallet, ChevronRight } from 'lucide-react';
import { getLLMStatus, getModelCatalog, LLMModelCatalogItem, modelBillingLabel, sanitizeModelDescription, filterVisibleCatalogModels } from '@/api/llm';
import { byokApi, pickedModelIds } from '@/api/byok';
import { useByokEnabled, useDisabledModels } from '@/hooks/useByokEnabled';
import { cloudApi, cloudBillingApi } from '@/api/cloud';
import { useModelDisplayName } from '@/hooks/useModelDisplayName';
import { useSettings } from '@/store/settings';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useLLMBalance } from '@/hooks/useLLMBalance';

const PROVIDER_COLORS: Record<string, string> = {
  ollama: 'bg-success',
  kimi: 'bg-fusion-primary',
  deepseek: 'bg-danger',
  opencode: 'bg-network-primary',
};

interface LLMConnectionStatusProps {
  placement?: 'top' | 'bottom';
  onLoginClick?: () => void;
  /** 紧凑模式（顶栏用）：只留状态点+延迟，隐藏 provider/model 文字（完整信息在 title 与弹层） */
  compact?: boolean;
}

const LLMConnectionStatus: FC<LLMConnectionStatusProps> = ({ placement = 'bottom', onLoginClick, compact = false }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoggedIn } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const setActiveProvider = useSettings((state) => state.setActiveProvider);
  const syncActiveProvider = useSettings((state) => state.syncActiveProvider);
  const activeProvider = useSettings((state) => state.activeProvider);
  const activeModel = useSettings((state) => state.activeModel);
  // 全局写标记：ChatInputBar/AISettings 发起的选型写也要纳入本组件同步 effect 的
  // 避让窗口（本地 lastUserActionRef/writePendingRef 只认右下角自己的点击）
  const lastActiveWriteAt = useSettings((state) => state.lastActiveWriteAt);
  const activeWritePending = useSettings((state) => state.activeWritePending);

  const { data: catalog } = useQuery({
    queryKey: ['llmModelCatalog'],
    queryFn: getModelCatalog,
    staleTime: 5 * 60 * 1000,
  });

  const { balance: balanceSummary } = useLLMBalance();

  // 平台模型（云端按量）：桌面端已绑定云端账号时并入模型列表（此前控制台只有本地目录，
  // 「右下角 LLM 控制台无系统模型」的根因）
  const isDesktop = !!window.psbDesktop?.isDesktop;
  const { data: cloudStatus } = useQuery({
    queryKey: ['cloudStatus'],
    queryFn: cloudApi.getStatus,
    enabled: isDesktop,
    staleTime: 30000,
  });
  const platformEnabled = isDesktop && !!cloudStatus?.bound;
  const { data: cloudCatalog } = useQuery({
    queryKey: ['cloudModelCatalog'],
    queryFn: cloudBillingApi.catalog,
    enabled: platformEnabled,
    staleTime: 5 * 60 * 1000,
  });
  // 平台模型的余额门必须看云端余额（cloud-proxy 链路）：本地 /billing/balance
  // 在桌面端被 require_platform_billing 恒 404（本机无平台计费是设计），
  // 读它等于永远「余额未知」——此前付费平台模型一点就跳充值页的根因
  const { data: cloudBalance } = useQuery({
    queryKey: ['cloudBalance'],
    queryFn: cloudBillingApi.balance,
    enabled: platformEnabled,
    staleTime: 60000,
  });
  const platformModels = platformEnabled
    ? (cloudCatalog?.models || []).filter(
        (m) => m.is_system || (m.price_input_per_1k || 0) > 0 || (m.price_output_per_1k || 0) > 0
      )
    : [];

  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ['llmStatus'],
    queryFn: () => getLLMStatus(),
    refetchInterval: 30000,
    staleTime: 30000,
  });

  // 显示以本地 store 为准：用户刚在控制台点选的模型立即生效；
  // 后端 status 只用于首次载入/跨设备同步（见下方同步 effect）以及连接状态。
  // active_provider 是 slug（身份），显示名从 providers 列表的映射取
  const providerDisplayMap = useMemo(() => {
    const map: Record<string, string> = {};
    status?.providers?.forEach((p) => {
      map[(p.slug || p.provider).toLowerCase()] = p.provider;
    });
    return map;
  }, [status]);
  const _activeSlug = (activeProvider || status?.active_provider || '').toLowerCase();
  const activeProviderName = _activeSlug === 'platform'
    ? '平台'
    : (providerDisplayMap[_activeSlug] || status?.active_provider_name || activeProvider || 'Ollama');
  // 只隐藏 opencode 聚合商名，其余厂商（ollama/deepseek/kimi）按原名显示
  const displayProviderName = activeProviderName.toLowerCase() === 'opencode' ? '' : activeProviderName;
  const _rawDisplayModel = activeModel || status?.active_model || 'qwen3.5:0.8b';
  // 平台模型显示目录名而非裸 id（platform:opencode-xxx → Ling-3.0-tiny Free）
  const _catalogDisplayName = useModelDisplayName(
    activeProvider === 'platform' ? `platform:${_rawDisplayModel}` : undefined
  );
  const displayModel = activeProvider === 'platform' ? (_catalogDisplayName || _rawDisplayModel) : _rawDisplayModel;
  // 系统行（平台按量/免费）选中态：头部只显示模型名，通道标注「平台通道」——
  // 不显示厂商名，也不显示 BYOK 连通状态（未连接是对平台通道的误报）
  const _activeRowId = activeModel || status?.active_model || '';
  const activeCatalogRow = (catalog?.models || []).find((m) => m.id === _activeRowId);
  const isPlatformChannel = activeProvider === 'platform' || !!activeCatalogRow?.is_system;
  const headerModelName = isPlatformChannel
    ? (activeCatalogRow?.name || displayModel)
    : displayModel;
  const isConnected = status?.connected ?? false;
  const activeLatency = status?.latency ?? -1;
  const statusReason = status?.reason || '';
  const modelUnavailable = statusReason.includes('此模型暂不可用');

  const activeProviderInfo = status?.providers?.find(
    (p) => p.provider.toLowerCase() === activeProviderName.toLowerCase()
  ) || {
    provider: activeProviderName,
    model: displayModel,
    connected: isConnected,
    latency: activeLatency,
    icon_color: PROVIDER_COLORS[activeProviderName.toLowerCase()] || 'bg-success',
    available: true,
  };

  const setMutation = useMutation({
    mutationFn: async ({ provider, model }: { provider: string; model: string }) => {
      // 写库期间挂起：慢写（>5s 保护窗）时，30s 轮询/refetch 回来的旧 status
      // 不允许回写 store——同步 effect 看 writePendingRef 直接跳过
      writePendingRef.current = true;
      await setActiveProvider(provider, model);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llmStatus'] });
      setIsOpen(false);
    },
    onError: () => {
      // 写库失败：清除保护窗口，让 refetch 回来的服务端状态立刻回滚乐观更新
      lastUserActionRef.current = 0;
      queryClient.invalidateQueries({ queryKey: ['llmStatus'] });
    },
    onSettled: () => {
      // 写库结束（成败都算）才解除挂起；成功路径随后 invalidate 的 refetch
      // 读到的是写库后的新值，不会再打回旧选择
      writePendingRef.current = false;
    },
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Sync backend active provider to local selector when status changes.
  // 只在 status 拿到新数据时运行（不把 store 变化作为触发条件）：
  // 否则用户刚点选新模型、store 已更新，这个 effect 会用 refetch 完成前的
  // 旧 status 把 store 打回旧模型——表现为"切换后刷新页面才生效"。
  const storeRef = useRef({ activeProvider, activeModel });
  storeRef.current = { activeProvider, activeModel };
  // 用户点选后的几秒内，拒绝用服务端 status 覆盖 store：
  // 覆盖写库 + refetch 的完成窗口，彻底避免旧 status 把新选择打回去
  const lastUserActionRef = useRef(0);
  // 写库进行中的硬门：5s 时间窗对慢写无效（写库超过 5s 时，轮询回来的旧 status
  // 照样回写 store），pending 期间同步 effect 一律跳过，不看时间
  const writePendingRef = useRef(false);
  useEffect(() => {
    if (writePendingRef.current) return;
    // 任意选择点（ChatInputBar/AISettings/本组件）的写在途或近写窗口内：
    // refetch 回来的可能是写库前的旧 status，一律不许回写 store（「差一层」根因）
    if (activeWritePending) return;
    if (Date.now() - lastUserActionRef.current < 5000) return;
    if (Date.now() - lastActiveWriteAt < 5000) return;
    if (status?.active_provider && status?.active_model) {
      const { activeProvider: sp, activeModel: sm } = storeRef.current;
      const backendProvider = status.active_provider.toLowerCase();
      const backendModel = status.active_model;
      const settingsProvider = (sp || '').toLowerCase();
      if (backendProvider !== settingsProvider || backendModel !== sm) {
        syncActiveProvider(status.active_provider, status.active_model);
      }
    }
  }, [status, syncActiveProvider, activeWritePending, lastActiveWriteAt]);

  const handleModelClick = (model: LLMModelCatalogItem) => {
    if (!isLoggedIn) {
      onLoginClick?.();
      setIsOpen(false);
      return;
    }
    // Check balance for paid models - if balance not loaded yet, block to be safe
    // 仅网页端做余额前置拦截：桌面端 catalog 全是 BYOK 自付（后端已把单价清零），
    // 平台按量模型走下方专门的平台区（handlePlatformModelClick 有同样的门）。
    // 此前桌面端点外部模型被余额门直接弹去充值页——BYOK 用的是用户自己的 key，不该拦。
    // 免费模型（价格为 0）不拦：每日免费额度/零价模型不需要余额，耗尽由服务端 402 在使用处提示；
    // 余额暂未加载也不硬拦（本地余额接口在桌面端 404 是设计，不能当成「没钱」）。
    if (!isDesktop && !isLocalModel(model.provider)) {
      const priced = (model.price_input_per_1k || 0) > 0 || (model.price_output_per_1k || 0) > 0;
      if (priced && balanceSummary && balanceSummary.balance < 0.1) {
        setIsOpen(false);
        navigate('/topup');
        return;
      }
    }
    const activeModelName = model.provider === 'ollama' ? model.provider_model_id : model.provider_model_id;
    // 乐观更新：先改本地 store，UI 立即跟随，再异步写库
    lastUserActionRef.current = Date.now();
    syncActiveProvider(model.provider, activeModelName);
    setMutation.mutate({ provider: model.provider, model: activeModelName });
  };

  // 平台模型：全局控制台选中即写入 active_provider=platform（云端按量，
  // 聊天经 platform: 通道转发；余额门槛与付费模型一致）
  const handlePlatformModelClick = (model: LLMModelCatalogItem) => {
    if (!isLoggedIn) {
      onLoginClick?.();
      setIsOpen(false);
      return;
    }
    // 余额门只拦有定价的平台模型：免费池模型（每日免费额度，价格为 0）不需要余额，
    // 耗尽后由服务端 402 在使用处提示——此前免费模型也因余额不足被弹去充值页。
    // 门看云端余额（cloud-proxy）：本地 /billing/balance 桌面端恒 404（本机无平台计费是设计），
    // 读它会让付费平台模型永远误跳充值页。余额未加载不硬拦，服务端 402 兜底。
    const priced = (model.price_input_per_1k || 0) > 0 || (model.price_output_per_1k || 0) > 0;
    if (priced && cloudBalance && cloudBalance.balance < 0.1) {
      setIsOpen(false);
      // 桌面端统一云端支付入口（本机 /topup 是死路由），window.open 由 Electron 交系统浏览器
      window.open('https://grzhishiku.com/topup', '_blank', 'noopener,noreferrer');
      return;
    }
    lastUserActionRef.current = Date.now();
    syncActiveProvider('platform', model.id);
    setMutation.mutate({ provider: 'platform', model: model.id });
    setIsOpen(false);
  };

  // 网页端系统行（平台按量/免费）点击：选中态写目录 id（与 BYOK 行的 provider_model_id
  // 区分开，通道不混、高亮不错位）；有价行余额不足指充值
  const handleSystemModelClick = (model: LLMModelCatalogItem) => {
    if (!isLoggedIn) {
      onLoginClick?.();
      setIsOpen(false);
      return;
    }
    const priced = (model.price_input_per_1k || 0) > 0 || (model.price_output_per_1k || 0) > 0;
    if (priced && balanceSummary && balanceSummary.balance < 0.1) {
      setIsOpen(false);
      navigate('/topup');
      return;
    }
    lastUserActionRef.current = Date.now();
    syncActiveProvider(model.provider, model.id);
    setMutation.mutate({ provider: model.provider, model: model.id });
    setIsOpen(false);
  };

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const fresh = await getLLMStatus(true);
      queryClient.setQueryData(['llmStatus'], fresh);
    } catch {
      await refetch(); // 强刷失败时保留常规查询的错误/重试口径
    } finally {
      setRefreshing(false);
    }
  };

  const formatLatency = (latency: number) => (latency >= 0 ? `${latency}ms` : '未连接');

  // Group catalog models by provider（BYOK 总开关关闭时只留本地 ollama；单模型开关再过滤）。
  // 两套分离：厂商分组只放 BYOK 行（非系统行）；系统行（is_system，平台按量/免费）
  // 一律在下方「系统模型」区——桌面端走云端目录平台分组，网页端用本地目录系统行
  const byokEnabled = useByokEnabled();
  const disabledModels = useDisabledModels();
  // BYOK 已选清单（选择器/控制台可见性口径：填了 key 且选了的才显示）
  const { data: byokPresetData } = useQuery({
    queryKey: ['byokPresets'],
    queryFn: byokApi.listPresets,
    staleTime: 30000,
  });
  const byokPicked = useMemo(() => pickedModelIds(byokPresetData), [byokPresetData]);
  const modelsByProvider = useMemo(() => {
    const groups: Record<string, LLMModelCatalogItem[]> = {};
    filterVisibleCatalogModels(catalog?.models || [], byokEnabled, disabledModels, isDesktop, byokPicked)
      .filter((m) => !m.is_system)
      .forEach((m) => {
        if (!groups[m.provider]) groups[m.provider] = [];
        groups[m.provider].push(m);
      });
    return groups;
  }, [catalog, byokEnabled, disabledModels, isDesktop, byokPicked]);

  // 网页端系统模型区（桌面端由云端目录平台分组承载）
  const webSystemModels = useMemo(
    () => (isDesktop ? [] : (catalog?.models || []).filter((m) => m.is_system)),
    [catalog, isDesktop]
  );

  const isLocalModel = (provider: string) => provider.toLowerCase() === 'ollama';

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 ${compact ? 'px-2' : 'px-3'} py-2 rounded-[2px] text-xs font-medium transition-all duration-300 glass ${
          isConnected
            ? isPlatformChannel ? 'text-info border-info/20' : 'text-success border-success/20'
            : 'text-danger border-danger/20'
        }`}
        title={`LLM: ${isPlatformChannel ? headerModelName : [displayProviderName, displayModel].filter(Boolean).join(' ')} | ${isConnected ? '已连接' : statusReason || '断开'}`}
      >
        <div className="relative">
          <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${activeProviderInfo.icon_color}`} />
          {isConnected && (
            <div className="absolute inset-0 w-2 h-2 rounded-full bg-success animate-ping opacity-40" />
          )}
        </div>

        {/* provider/model 文字：compact（顶栏）下整块不渲染，完整信息在 title tooltip 与弹层 */}
        {!compact && (
        <span className="hidden lg:inline">
          <span className="text-text-primary font-semibold">
            {isPlatformChannel ? headerModelName : displayProviderName}
          </span>
          <span className="text-text-muted ml-1">{isPlatformChannel ? '' : displayModel}</span>
        </span>
        )}

        {isConnected && activeLatency >= 0 && (
          <span className="text-text-muted">{activeLatency}ms</span>
        )}

        {isLoading && (
          <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute right-0 ${placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} w-[460px] glass-popup rounded-[2px] p-4 z-50`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-info" />
                <span className="text-sm font-semibold text-text-primary">LLM 模型控制台</span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-[2px] hover:bg-white/[0.06] text-text-muted hover:text-text-primary transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Active Model Card */}
            <div className="glass-card p-4 mb-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-10 h-10 rounded-[2px] bg-gradient-to-br ${activeProviderInfo.icon_color} flex items-center justify-center`}>
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-text-primary">
                      {isPlatformChannel ? headerModelName : displayProviderName}
                    </div>
                    <div className="text-xs text-text-muted">
                      {isPlatformChannel ? '平台通道（按量）' : displayModel}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={isLoading || refreshing}
                  className="p-1.5 rounded-[2px] hover:bg-white/[0.06] text-text-muted hover:text-info transition-colors disabled:opacity-50"
                  title="刷新状态"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading || refreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isConnected ? (
                    <>
                      <div className="relative">
                        <div className={`w-2 h-2 rounded-full ${isPlatformChannel ? 'bg-info' : 'bg-success'}`} />
                        <div className={`absolute inset-0 w-2 h-2 rounded-full opacity-40 ${isPlatformChannel ? 'bg-info' : 'bg-success'}`} />
                      </div>
                      <span className={`text-xs font-medium ${isPlatformChannel ? 'text-info' : 'text-success'}`}>
                        {isPlatformChannel ? '平台通道已连接' : '已连接'}
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 rounded-full bg-danger" />
                      <span className="text-xs font-medium text-danger">
                        {modelUnavailable ? '此模型暂不可用' : '未连接'}
                      </span>
                    </>
                  )}
                </div>
                <span className="text-xs text-text-muted">
                  {isConnected ? formatLatency(activeProviderInfo.latency) : ''}
                </span>
              </div>
              {!isConnected && statusReason && !modelUnavailable && (
                <div className="mt-2 text-xs leading-relaxed text-danger/90 break-words">
                  原因：{statusReason}
                </div>
              )}
            </div>

            {/* Balance display - only show when sufficient, hide warning */}
            {!isLocalModel(activeProviderName) && balanceSummary && balanceSummary.balance >= 0.1 && (
              <div className="mb-3 flex items-center gap-1.5 text-xs text-text-muted bg-bg-secondary/50 rounded-[2px] px-3 py-2">
                <Wallet className="w-3 h-3 text-success" />
                <span>可用余额 ¥{balanceSummary.balance.toFixed(2)}</span>
              </div>
            )}

            {/* Model List by Provider */}
            <div className="space-y-3 max-h-72 overflow-y-auto pr-0.5">
              {Object.entries(modelsByProvider).map(([provider, models]) => {
                return (
                  <div key={provider} className="space-y-0.5">
                    {/* Models under this provider - no provider header shown */}
                    <div className="divide-y divide-white/[0.04] border border-white/[0.06] rounded-[2px] overflow-hidden">
                      {models.map((model) => {
                        // 选中态按行精确匹配：BYOK 行=provider+provider_model_id；平台通道
                        // （activeProvider=platform）和系统行（目录 id）都不点亮 BYOK 行
                        const isActive = activeProvider !== 'platform'
                          && model.provider.toLowerCase() === activeProviderName.toLowerCase()
                          && model.provider_model_id === displayModel;
                        const isSetting = setMutation.isPending && setMutation.variables?.provider === model.provider && setMutation.variables?.model === model.provider_model_id;
                        const priceLabel = modelBillingLabel(model);

                        return (
                          <button
                            key={model.id}
                            onClick={() => handleModelClick(model)}
                            disabled={setMutation.isPending}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all ${
                              isActive
                                ? 'bg-info/10 text-info'
                                : 'hover:bg-white/[0.03] text-text-secondary'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-text-primary truncate">{model.name}</span>
                                {isActive && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-info/20 text-info border border-info/20">当前</span>
                                )}
                              </div>
                              <div className="text-xs text-text-muted truncate">{sanitizeModelDescription(model.description)}</div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-[10px] text-text-muted">{priceLabel}</span>
                              {isSetting ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-info" />
                              ) : (
                                <ChevronRight className="w-3 h-3 text-text-muted" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {platformModels.length > 0 && (
                <div className="space-y-0.5">
                  <div className="px-1 text-[10px] text-text-muted">☁️ 系统模型（平台按量/免费）</div>
                  <div className="divide-y divide-white/[0.04] border border-white/[0.06] rounded-[2px] overflow-hidden">
                    {platformModels.map((model) => {
                      const isActive = activeProvider === 'platform' && activeModel === model.id;
                      const isSetting = setMutation.isPending && setMutation.variables?.provider === 'platform' && setMutation.variables?.model === model.id;
                      const platformDesc = sanitizeModelDescription(model.description);
                      return (
                        <button
                          key={model.id}
                          onClick={() => handlePlatformModelClick(model)}
                          disabled={setMutation.isPending}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all ${
                            isActive ? 'bg-info/10 text-info' : 'hover:bg-white/[0.03] text-text-secondary'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-text-primary truncate">{model.name}</span>
                              {isActive && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-info/20 text-info border border-info/20">当前</span>
                              )}
                            </div>
                            {platformDesc && (
                              <div className="text-xs text-text-muted truncate">{platformDesc}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-[10px] text-text-muted">{modelBillingLabel(model)}</span>
                            {isSetting ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-info" />
                            ) : (
                              <ChevronRight className="w-3 h-3 text-text-muted" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {webSystemModels.length > 0 && (
                <div className="space-y-0.5">
                  <div className="px-1 text-[10px] text-text-muted">☁️ 系统模型（平台按量/免费）</div>
                  <div className="divide-y divide-white/[0.04] border border-white/[0.06] rounded-[2px] overflow-hidden">
                    {webSystemModels.map((model) => {
                      // 系统行选中态按目录 id 精确匹配（BYOK 行按 provider_model_id），不再双亮
                      const isActive = activeProvider !== 'platform' && activeModel === model.id;
                      const isSetting = setMutation.isPending && setMutation.variables?.provider === model.provider && setMutation.variables?.model === model.id;
                      const sysDesc = sanitizeModelDescription(model.description);
                      return (
                        <button
                          key={model.id}
                          onClick={() => handleSystemModelClick(model)}
                          disabled={setMutation.isPending}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all ${
                            isActive ? 'bg-info/10 text-info' : 'hover:bg-white/[0.03] text-text-secondary'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-text-primary truncate">{model.name}</span>
                              {isActive && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-info/20 text-info border border-info/20">当前</span>
                              )}
                            </div>
                            {sysDesc && (
                              <div className="text-xs text-text-muted truncate">{sysDesc}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-[10px] text-text-muted">{modelBillingLabel(model)}</span>
                            {isSetting ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-info" />
                            ) : (
                              <ChevronRight className="w-3 h-3 text-text-muted" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!catalog?.models?.length && (
                <div className="text-center py-4 text-xs text-text-muted">
                  暂无可用的模型配置
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-3 pt-3 border-t border-white/[0.08] flex items-center justify-between">
              {/* 智能路由恒开（路由自动按内容/通道选模型）——此前是可点开关但不持久化
                  不上送，纯死控件（10-01 BUG-10）：改被动指示，不给假开关 */}
              <div className="flex items-center gap-2 text-xs text-text-muted" title="路由自动按内容与可用通道选模型，恒为开启">
                <Sparkles className="w-3.5 h-3.5 text-fusion-primary" />
                <span>智能路由 · 自动</span>
              </div>

              <button
                onClick={() => {
                  setIsOpen(false);
                  if (!isLoggedIn) {
                    onLoginClick?.();
                  } else {
                    navigate('/settings');
                  }
                }}
                className="flex items-center gap-1.5 text-xs text-info hover:text-network-secondary transition-colors"
              >
                <Settings className="w-3.5 h-3.5" />
                AI 设置
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LLMConnectionStatus;
